import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, InputNumber, message, Popconfirm, Space } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { productApi, type ProductDTO } from '../api/kfc';
import ProductImportModal from '../components/ProductImportModal';
import { clientTablePagination } from '../utils/tablePagination';

const ProductManage: React.FC = () => {
  const [list, setList] = useState<ProductDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    productApi
      .list()
      .then((res) => setList(res.data))
      .catch(() => {
        setList([]);
        message.warning('无法加载产品列表');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (r: ProductDTO) => {
    setEditingId(r.id ?? null);
    form.setFieldsValue({
      hscode: r.hscode,
      nameZN: r.nameZN,
      nameEN: r.nameEN,
      brand: r.brand,
      model: r.model,
      picture: r.picture,
      containsBattery: r.containsBattery,
      containsMagnet: r.containsMagnet,
      usageZhCN: r.usage?.zh_CN,
      usageEnUS: r.usage?.en_US,
      materialZhCN: r.material?.zh_CN,
      materialEnUS: r.material?.en_US,
      priceUSD: r.price?.USD,
      priceRMB: r.price?.RMB,
    });
    setModalOpen(true);
  };

  const onFinish = (values: Record<string, any>) => {
    const dto: Partial<ProductDTO> = {
      hscode: values.hscode,
      nameZN: values.nameZN,
      nameEN: values.nameEN,
      brand: values.brand,
      model: values.model,
      picture: values.picture,
      containsBattery: values.containsBattery,
      containsMagnet: values.containsMagnet,
      usage: { zh_CN: values.usageZhCN, en_US: values.usageEnUS },
      material: { zh_CN: values.materialZhCN, en_US: values.materialEnUS },
      price: { USD: values.priceUSD, RMB: values.priceRMB },
    };
    const submit = editingId ? productApi.update(editingId, dto) : productApi.create(dto);
    submit
      .then(() => {
        message.success(editingId ? '更新成功' : '创建成功');
        setModalOpen(false);
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '操作失败'));
  };

  const onDelete = (id: string) => {
    productApi
      .delete(id)
      .then(() => {
        message.success('删除成功');
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '删除失败'));
  };

  const downloadTemplate = () => {
    productApi
      .downloadTemplate()
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'kfy-product-list.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => message.error('下载模板失败'));
  };

  const renderLocalized = (obj?: { zh_CN?: string; en_US?: string }) => {
    if (!obj) return '-';
    const parts = [];
    if (obj.zh_CN) parts.push(obj.zh_CN);
    if (obj.en_US) parts.push(`[EN] ${obj.en_US}`);
    return parts.length ? parts.join(' / ') : '-';
  };

  const renderPrice = (price?: { USD?: number; RMB?: number }) => {
    if (!price) return '-';
    const parts = [];
    if (price.USD != null) parts.push(`$${price.USD}`);
    if (price.RMB != null) parts.push(`¥${price.RMB}`);
    return parts.length ? parts.join(' / ') : '-';
  };

  return (
    <>
      <Card
        title="产品列表"
        extra={
          <Space>
            <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
              下载模板
            </Button>
            <Button icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>
              导入
            </Button>
            <Button type="primary" onClick={openAdd}>
              新增产品
            </Button>
          </Space>
        }
      >
        <Table
          loading={loading}
          dataSource={list}
          rowKey="id"
          scroll={{ x: 1400 }}
          pagination={clientTablePagination()}
          columns={[
            { title: '名称(英)', dataIndex: 'nameEN', width: 120, ellipsis: true },
            { title: '名称(中)', dataIndex: 'nameZN', width: 100, ellipsis: true },
            { title: '海关编码', dataIndex: 'hscode', width: 110, ellipsis: true },
            { title: '品牌', dataIndex: 'brand', width: 80, ellipsis: true },
            { title: '型号', dataIndex: 'model', width: 100, ellipsis: true },
            { title: '内置电', dataIndex: 'containsBattery', width: 80, ellipsis: true },
            { title: '带磁', dataIndex: 'containsMagnet', width: 70, ellipsis: true },
            {
              title: '图片',
              dataIndex: 'picture',
              width: 64,
              render: (v: string) =>
                v ? (
                  <img
                    src={v.startsWith('data:') || v.startsWith('http') ? v : `data:image/png;base64,${v}`}
                    alt=""
                    style={{ width: 40, height: 40, objectFit: 'cover' }}
                  />
                ) : (
                  '-'
                ),
            },
            { title: '用途', key: 'usage', width: 140, ellipsis: true, render: (_, r) => renderLocalized(r.usage) },
            { title: '材质', key: 'material', width: 140, ellipsis: true, render: (_, r) => renderLocalized(r.material) },
            { title: '价格', key: 'price', width: 120, render: (_, r) => renderPrice(r.price) },
            {
              title: '操作',
              width: 140,
              fixed: 'right',
              render: (_, r) => (
                <>
                  <Button type="link" size="small" onClick={() => openEdit(r)}>
                    编辑
                  </Button>
                  <Popconfirm
                    title="确定要删除该产品吗？"
                    onConfirm={() => r.id && onDelete(r.id)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button type="link" size="small" danger>
                      删除
                    </Button>
                  </Popconfirm>
                </>
              ),
            },
          ]}
        />
      </Card>
      <Modal
        title={editingId ? '编辑产品' : '新增产品'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="hscode"
            label="海关编码"
            rules={[{ required: true, message: '请输入海关编码' }]}
          >
            <Input placeholder="海关编码" disabled={!!editingId} />
          </Form.Item>
          <Form.Item
            name="nameEN"
            label="产品名称（英文）"
            rules={[{ required: true, message: '请输入英文名称' }]}
          >
            <Input placeholder="名称英文" disabled={!!editingId} />
          </Form.Item>
          <Form.Item name="nameZN" label="产品名称（中文）">
            <Input placeholder="名称中文" />
          </Form.Item>
          <Form.Item name="brand" label="品牌">
            <Input placeholder="品牌" />
          </Form.Item>
          <Form.Item name="model" label="型号">
            <Input placeholder="型号" />
          </Form.Item>
          <Form.Item name="containsBattery" label="内置电/配置电">
            <Input placeholder="如：无电、内置电" />
          </Form.Item>
          <Form.Item name="containsMagnet" label="带磁">
            <Input placeholder="如：带磁、不带磁" />
          </Form.Item>
          <Form.Item name="picture" label="图片（Base64 或 URL）">
            <Input.TextArea placeholder="图片 base64、URL 或留空" rows={3} />
          </Form.Item>
          <Form.Item name="usageZhCN" label="用途（中文）">
            <Input placeholder="用途中文描述" />
          </Form.Item>
          <Form.Item name="usageEnUS" label="用途（英文）">
            <Input placeholder="用途英文描述" />
          </Form.Item>
          <Form.Item name="materialZhCN" label="材质（中文）">
            <Input placeholder="材质中文描述" />
          </Form.Item>
          <Form.Item name="materialEnUS" label="材质（英文）">
            <Input placeholder="材质英文描述" />
          </Form.Item>
          <Form.Item name="priceUSD" label="价格（USD）">
            <InputNumber placeholder="美元" min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="priceRMB" label="价格（RMB）">
            <InputNumber placeholder="人民币" min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, marginTop: 16 }}>
            <Button type="primary" htmlType="submit">
              保存
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => setModalOpen(false)}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      <ProductImportModal open={importOpen} onClose={() => setImportOpen(false)} onSuccess={load} />
    </>
  );
};

export default ProductManage;
